import { fitQuoteFontSize } from './fitQuoteFontSize';
import type { QuoteSlideStyle, QuoteTextPosition } from './types';

/**
 * Geometry of the quote text on a slide — shared by the renderer
 * (`QuoteSlide`) and the drag overlay, so the box the user grabs is the box
 * that is drawn. Everything is in composition pixels; `measureWord` returns
 * a word's advance width at a font size in the ACTUAL slide font (canvas
 * `measureText` in the browser), so a wide brand font wraps and fits the way
 * it really renders.
 */

export const QUOTE_LINE_HEIGHT = 1.25;
export const QUOTE_MAX_FONT_SIZE = 96;
/** Floor for the binary search. Low enough that a 220-char quote in a wide
 *  font with a thick outline still fits the smallest box, so the fit (never
 *  a clip) always wins. */
export const QUOTE_MIN_FONT_SIZE = 20;
/** Fraction of the box width kept free on top of the outline, absorbing
 *  canvas-vs-layout-engine rounding differences. */
export const QUOTE_TEXT_SAFETY_MARGIN = 0.04;
export const QUOTE_PADDING_FRACTION = 0.08;
export const QUOTE_CARD_BOX_HEIGHT_FRACTION = 0.6;
export const QUOTE_FRAME_BOX_HEIGHT_FRACTION = 0.33;
/** Card style's decorative opening quote mark, fraction of the slide width. */
export const QUOTE_MARK_SIZE_FRACTION = 0.16;
export const QUOTE_SIZE_SCALE_MIN = 0.7;
export const QUOTE_SIZE_SCALE_MAX = 1.3;

export type MeasureWord = (word: string, fontSize: number) => number;

/**
 * Greedy word wrap at `maxWidth`. A word wider than `maxWidth` (an
 * unbreakable token) counts as `ceil(width / maxWidth)` lines — the rendered
 * box sets `overflow-wrap: break-word` — so a long single word can never
 * "fit" in one line at any size.
 */
export function wrapQuoteText(args: {
  text: string;
  fontSize: number;
  maxWidth: number;
  measureWord: MeasureWord;
}): { lines: number; maxLineWidth: number } {
  const {
    text, fontSize, maxWidth, measureWord,
  } = args;
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return { lines: 1, maxLineWidth: 0 };
  const spaceWidth = measureWord(' ', fontSize);

  let lines = 1;
  let lineWidth = 0;
  let maxLineWidth = 0;
  const startLine = (width: number) => {
    if (width > maxWidth) {
      const wordLines = Math.ceil(width / maxWidth);
      lines += wordLines - 1;
      maxLineWidth = Math.max(maxLineWidth, maxWidth);
      lineWidth = width - (wordLines - 1) * maxWidth;
    } else {
      lineWidth = width;
    }
    maxLineWidth = Math.max(maxLineWidth, lineWidth);
  };

  words.forEach((word, index) => {
    const width = measureWord(word, fontSize);
    if (index === 0) {
      startLine(width);
      return;
    }
    const candidate = lineWidth + spaceWidth + width;
    if (candidate <= maxWidth) {
      lineWidth = candidate;
      maxLineWidth = Math.max(maxLineWidth, lineWidth);
    } else {
      lines += 1;
      startLine(width);
    }
  });

  return { lines, maxLineWidth };
}

/** Outline thickness in px (the outline is drawn OUTSIDE the glyphs). */
export function quoteOutlinePx(fontSize: number, outlinePct: number): number {
  return Math.max(0, outlinePct) * fontSize / 100;
}

/** Width the words may occupy: the box minus the outline on both sides and
 *  the safety margin. */
export function quoteWrapWidth(boxWidth: number, fontSize: number, outlinePct: number): number {
  return Math.max(1, boxWidth * (1 - QUOTE_TEXT_SAFETY_MARGIN) - 2 * quoteOutlinePx(fontSize, outlinePct));
}

function measureTextBlock(args: {
  text: string;
  fontSize: number;
  boxWidth: number;
  outlinePct: number;
  measureWord: MeasureWord;
}) {
  const {
    text, fontSize, boxWidth, outlinePct, measureWord,
  } = args;
  const outline = quoteOutlinePx(fontSize, outlinePct);
  const wrap = wrapQuoteText({
    text, fontSize, maxWidth: quoteWrapWidth(boxWidth, fontSize, outlinePct), measureWord,
  });
  return {
    lines: wrap.lines,
    maxLineWidth: wrap.maxLineWidth,
    outline,
    height: wrap.lines * fontSize * QUOTE_LINE_HEIGHT + 2 * outline,
  };
}

/** Largest font size (up to the size-scaled maximum) whose wrapped text,
 *  outline included, fits `boxWidth × boxHeight`. */
export function fitQuoteText(args: {
  text: string;
  boxWidth: number;
  boxHeight: number;
  outlinePct?: number;
  /** 0.7–1.3: multiplies the maximum; the fit is still enforced. */
  sizeScale?: number;
  measureWord: MeasureWord;
}): number {
  const {
    text, boxWidth, boxHeight, outlinePct = 0, sizeScale = 1, measureWord,
  } = args;
  const scale = Math.min(QUOTE_SIZE_SCALE_MAX, Math.max(QUOTE_SIZE_SCALE_MIN, Number.isFinite(sizeScale) ? sizeScale : 1));
  const maxFontSize = Math.round(QUOTE_MAX_FONT_SIZE * scale);
  return fitQuoteFontSize({
    text,
    boxHeight,
    maxFontSize,
    minFontSize: Math.min(QUOTE_MIN_FONT_SIZE, maxFontSize),
    measure: (fontSize) => measureTextBlock({
      text, fontSize, boxWidth, outlinePct, measureWord,
    }).height,
  });
}

export interface QuoteTextLayout {
  padding: number;
  fontSize: number;
  outline: number;
  /** Card only: height of the opening quote mark row (0 for frame). */
  quoteMarkSize: number;
  /** Positioned text block (quote mark + text), px, inside the padding. */
  block: { left: number; top: number; width: number; height: number };
  /** Effective (clamped) center of the block, fractions of the slide. */
  center: QuoteTextPosition;
}

const clamp = (value: number, lo: number, hi: number) => (hi < lo ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, value)));

/** Keeps a block of `blockWidth × blockHeight` px centered as close to
 *  `center` (fractions) as the slide padding allows. Returns px. */
export function clampTextCenterPx(args: {
  center: QuoteTextPosition;
  blockWidth: number;
  blockHeight: number;
  width: number;
  height: number;
  padding: number;
}): { x: number; y: number } {
  const {
    center, blockWidth, blockHeight, width, height, padding,
  } = args;
  return {
    x: clamp(center.x * width, padding + blockWidth / 2, width - padding - blockWidth / 2),
    y: clamp(center.y * height, padding + blockHeight / 2, height - padding - blockHeight / 2),
  };
}

export function computeQuoteTextLayout(args: {
  style: QuoteSlideStyle;
  width: number;
  height: number;
  text: string;
  sizeScale: number;
  outlinePct: number;
  textPosition: QuoteTextPosition | null;
  measureWord: MeasureWord;
}): QuoteTextLayout {
  const {
    style, width, height, text, sizeScale, outlinePct, textPosition, measureWord,
  } = args;
  const padding = width * QUOTE_PADDING_FRACTION;
  const boxWidth = width - padding * 2;
  const boxHeight = height * (style === 'card' ? QUOTE_CARD_BOX_HEIGHT_FRACTION : QUOTE_FRAME_BOX_HEIGHT_FRACTION);

  const fontSize = fitQuoteText({
    text, boxWidth, boxHeight, outlinePct, sizeScale, measureWord,
  });
  const measured = measureTextBlock({
    text, fontSize, boxWidth, outlinePct, measureWord,
  });
  const quoteMarkSize = style === 'card' ? width * QUOTE_MARK_SIZE_FRACTION : 0;
  // The text box hugs the widest line (plus outline and the safety slack)
  // so it can move sideways; the words wrap exactly as measured because the
  // slack is kept inside it.
  const slack = boxWidth * QUOTE_TEXT_SAFETY_MARGIN;
  const blockWidth = Math.min(boxWidth, Math.max(measured.maxLineWidth + slack + 2 * measured.outline, quoteMarkSize));
  const blockHeight = quoteMarkSize + measured.height;

  // Default: the frame style sits on the bottom padding, the card style is
  // centered — the two legacy layouts.
  const defaultCenter: QuoteTextPosition = style === 'frame'
    ? { x: 0.5, y: (height - padding - blockHeight / 2) / height }
    : { x: 0.5, y: 0.5 };
  const px = clampTextCenterPx({
    center: textPosition ?? defaultCenter,
    blockWidth,
    blockHeight,
    width,
    height,
    padding,
  });

  return {
    padding,
    fontSize,
    outline: measured.outline,
    quoteMarkSize,
    block: {
      left: px.x - blockWidth / 2,
      top: px.y - blockHeight / 2,
      width: blockWidth,
      height: blockHeight,
    },
    center: { x: px.x / width, y: px.y / height },
  };
}
